import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';

import { nanoidRegex } from '@mai/types';

import { useEmbeddingsQuery } from '@queries/embeddings';

const MarkdownContainer = styled.div`
  .markdown p {
    margin: 0.25rem 0;
  }
`;

const MarkdownContent = ({ content }: { content: string }) => {
  // Replace inline references with links to the
  const { formattedContent, embeddingIds } = useMemo(() => {
    // Match all with the forgiving regex to find the start and end offsets
    const embeddingIds = Array.from(
      new Set(
        Array.from(content.matchAll(nanoidRegex)).map((match) => match[0]!),
      ),
    );
    let formattedContent = content;
    for (const [index, embeddingId] of embeddingIds.entries()) {
      formattedContent = formattedContent.replaceAll(
        embeddingId,
        `[${index + 1}](/embeddings/${embeddingId})`,
      );
    }

    // Remove all instances of ","
    formattedContent = formattedContent.replaceAll('","', ', ');
    formattedContent = formattedContent.replaceAll('", "', ', ');

    // Remove all instances of [[" and "]]
    formattedContent = formattedContent.replaceAll('[["', '[');
    formattedContent = formattedContent.replaceAll('["', '[');
    formattedContent = formattedContent.replaceAll('"]]', ']');
    formattedContent = formattedContent.replaceAll('"]', ']');

    return { formattedContent, embeddingIds };
  }, [content]);

  const embeddingsQuery = useEmbeddingsQuery({
    embeddingIds,
  });

  return (
    <div>
      <MarkdownContainer>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ node: _, ...props }) => (
              <p
                {...props}
                style={{ marginTop: '0.25rem', marginBottom: '0.25rem' }}
              />
            ),
            a: ({ node: _, ...props }) => {
              // Use react router link if relative link
              if (props.href?.includes('/embeddings/')) {
                const embeddingId = props.href?.split('/embeddings/')[1];
                const embedding = embeddingsQuery.data?.find(
                  (embedding) => embedding.id === embeddingId,
                );
                return (
                  <Tooltip
                    title={
                      <div
                        style={{
                          maxWidth: '300px',
                          maxHeight: '300px',
                          overflow: 'scroll',
                        }}
                      >
                        {embedding?.content}
                      </div>
                    }
                  >
                    <Typography.Link
                      style={{
                        cursor: 'default',
                      }}
                    >
                      {props.children}
                    </Typography.Link>
                  </Tooltip>
                );
              }
              // Use normal anchor tag for external links
              return <a {...props} />;
            },
            h1: ({ node: _, ...props }) => (
              <Typography.Title
                level={2}
                style={{ marginTop: '1rem' }}
                {...props}
              />
            ),
            h2: ({ node: _, ...props }) => (
              <Typography.Title
                level={3}
                style={{ marginTop: '1rem' }}
                {...props}
              />
            ),
            h3: ({ node: _, ...props }) => (
              <Typography.Title
                level={4}
                style={{ marginTop: '1rem' }}
                {...props}
              />
            ),
            h4: ({ node: _, ...props }) => (
              <Typography.Title
                level={5}
                style={{ marginTop: '1rem' }}
                {...props}
              />
            ),
          }}
        >
          {formattedContent}
        </ReactMarkdown>
      </MarkdownContainer>
    </div>
  );
};

export default MarkdownContent;
